import type { LucideIcon } from "lucide-react";
import {
  Home,
  BookOpen,
  Users,
  ShieldCheck,
  LayoutGrid,
  Church,
  CalendarRange,
  CalendarDays,
  CalendarCheck,
  DoorOpen,
  Megaphone,
  UserRound,
} from "lucide-react";

/** Papel de acesso do usuário (DA-002), usado para resolver rotas por módulo. */
export interface AccessRole {
  ehAdminPlataforma: boolean;
  podeGerenciarBiblioteca: boolean;
  temAcessoAdministrativo: boolean;
}

export interface ModuleEntry {
  /** Casa com os códigos de `modulos_visiveis()`. "inicio" é sintético. */
  code: string;
  label: string;
  icon: LucideIcon;
  order: number;
  /** Resolve para uma rota EXISTENTE conforme o papel. */
  resolvePath: (r: AccessRole) => string;
}

export const INICIO_ENTRY: ModuleEntry = {
  code: "inicio",
  label: "Início",
  icon: Home,
  order: 0,
  resolvePath: () => "/inicio",
};

/**
 * Calendário Central (Sprint 3): entrada UNIVERSAL (agregador de leitura), não
 * é um módulo de `modulos_visiveis()`. Sempre visível para autenticados.
 */
export const CALENDARIO_ENTRY: ModuleEntry = {
  code: "calendario",
  label: "Calendário",
  icon: CalendarRange,
  order: 5,
  resolvePath: () => "/calendario",
};

/**
 * Registro dos módulos em código. Hoje `modulos_visiveis()` devolve só os
 * códigos; rótulo/ícone/ordem/rota vivem aqui até o banco expor esses
 * metadados. O drawer e o Início renderizam a partir daqui, filtrado por
 * visibilidade — nunca com módulos "chumbados".
 *
 * Skill v3.0 (UX limpa): os placeholders "Em breve" foram removidos — só
 * módulos REAIS entram aqui. Módulo novo = catálogo no banco + entrada aqui.
 */
export const MODULES_REGISTRY: ModuleEntry[] = [
  {
    code: "biblioteca",
    label: "Biblioteca",
    icon: BookOpen,
    order: 10,
    // DA-002: os guards continuam sendo o portão; isto só navega.
    // Quem tem acesso administrativo (gestor OU admin de plataforma) entra pelo
    // HUB do módulo (/admin); o membro comum vai direto para a sua visão.
    resolvePath: (r) => (r.temAcessoAdministrativo ? "/admin" : "/usuario"),
  },
  {
    code: "eventos",
    label: "Eventos",
    icon: CalendarDays,
    order: 20,
    // Todos veem o módulo (visibilidade todos_membros); a criação/edição é
    // gated pela RLS. A página abre a lista de eventos.
    resolvePath: () => "/eventos",
  },
  {
    code: "reservas",
    label: "Reservas",
    icon: DoorOpen,
    order: 30,
    resolvePath: () => "/reservas",
  },
  {
    code: "escala",
    label: "Escala",
    icon: CalendarCheck,
    order: 40,
    // Autorização de escrita é POR MINISTÉRIO (lidera_ou_gerencia_ministerio),
    // nunca um flag global como em Eventos/Reservas — a página de lista já
    // decide o que cada pessoa pode fazer a partir da RLS/RPCs reais.
    resolvePath: () => "/escalas",
  },
  {
    code: "comunicados",
    label: "Comunicados",
    icon: Megaphone,
    order: 50,
    // Todos veem o elo (a visibilidade é decidida pelo banco em modulos_visiveis());
    // criar/editar/arquivar/apagar é só do líder/gestor, revalidado pelas RPCs.
    resolvePath: () => "/comunicados",
  },
  {
    code: "visitantes",
    label: "Visitantes",
    icon: UserRound,
    order: 60,
    // Todos os papéis entram na mesma tela; o que cada um vê e pode fazer é decidido
    // pelo banco (RPCs do elo Visitantes): ministério responsável, líder de outro
    // ministério (modo consulta) ou administrador (somente leitura).
    resolvePath: () => "/visitantes",
  },
];

/** Funções do Núcleo (não são módulos de `modulos_visiveis`); gate próprio. */
export interface CoreAdminEntry {
  label: string;
  icon: LucideIcon;
  path: string;
  /** Texto curto do card no Início. */
  description: string;
}

/**
 * Skill v3.0: a tela "Acessos" foi EXTINTA — quem gerencia cada módulo se
 * define dentro do Ministério responsável (funções com pode_gerenciar_modulo).
 */
export const CORE_ADMIN_ENTRIES: CoreAdminEntry[] = [
  {
    label: "Usuários",
    icon: ShieldCheck,
    path: "/admin/usuarios",
    description: "Aprovar cadastros e definir perfis de acesso.",
  },
  {
    label: "Ministérios",
    icon: Users,
    path: "/admin/ministerios",
    description: "Ministérios, funções, integrantes e gestão de elos.",
  },
  {
    label: "Elos da igreja",
    icon: LayoutGrid,
    path: "/admin/modulos",
    description: "Ativar elos e definir quem os vê.",
  },
  {
    label: "Igreja",
    icon: Church,
    path: "/admin/igreja",
    description: "Informações administrativas da igreja.",
  },
];
